/**
 * Builds the parts payload for Expense Inventory sampling and persists it via the
 * createUpdateExpenseLineItems mutation, then reports the added parts to the caller.
 * @module src/components/Inventory/expense/detail/ExpenseInventorySampling/AddPartsModal/hook/useSaveExpenseInventoryParts
 */
import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import CREATE_UPDATE_EXPENSE_LINE_ITEMS from '../../../../../../../graphql/mutations/createUpdateExpenseLineItems';
import { NOTIFY_TYPE, STALE_RECORD_MESSAGE } from '../../../../../../../util/constants';
import { isStaleRecordError } from '../../../../../../../util/optimisticLock';
import { findPrefilledSavedLine, toAddPartsLineItems } from '../../samplingLineRules';
import { useSaveExpenseInventoryPartsProps } from './UseSaveExpenseInventoryPartsProps';

const useSaveExpenseInventoryParts = ({
  setIsSaving,
  expenseInventoryDetail,
  inventoryPartsToAdd,
  catalogPartsToAdd,
  handleClose,
  openSnackBar,
  onPartsAdded,
}: useSaveExpenseInventoryPartsProps) => {
  const [createUpdateExpenseLineItems, { loading: loadingLineItems }] = useMutation(
    CREATE_UPDATE_EXPENSE_LINE_ITEMS
  );

  const onAddClick = useCallback(async () => {
    setIsSaving(true);

    const lineItems: any[] = [];

    const sentSavedLineIds = new Set<string>();
    const savedLineItems = toAddPartsLineItems(expenseInventoryDetail?.lineItems);

    inventoryPartsToAdd.forEach((lotInfo: any) => {
      // Two inventory rows (same lot, different expiration dates) match the same saved line, and
      // only the first entry for a lot holds the latest counter value, so it alone decides the line.
      const savedLine = findPrefilledSavedLine(lotInfo, savedLineItems);
      const savedLineId = savedLine?.sfid || savedLine?.externalId;
      if (savedLineId && sentSavedLineIds.has(savedLineId)) {
        return;
      }
      if (savedLineId) {
        sentSavedLineIds.add(savedLineId);
      }

      if (!lotInfo.counterValue) {
        return;
      }

      if (savedLine && Number(savedLine.quantity) === Number(lotInfo.counterValue)) {
        return;
      }

      lineItems.push({
        lotNumber: lotInfo.lotNumber,
        productId: lotInfo.productId,
        productNumber: lotInfo.productNumber,
        productDescription: lotInfo.productDescription,
        baseorCapital: lotInfo.baseorCapital,
        quantity: lotInfo.counterValue.toString(),
        isLotNumber: lotInfo?.isLotNumber,
        subInventoryCode: lotInfo?.subInventoryCode || '',
        partAddedFrom: lotInfo?.sourceLocation,
        sourceLocation: lotInfo?.sourceLocation,
        expirationDate: lotInfo.expirationDate,
        maxQuantity: lotInfo.maxPermissibleValue,
        iscatalog: false,
        sfid: savedLine?.sfid,
        externalId: savedLine?.externalId,
      });
    });

    catalogPartsToAdd.forEach((lotInfo: any) => {
      if (lotInfo.changed) {
        lineItems.push({
          productId: lotInfo.productId,
          productNumber: lotInfo.productNumber,
          productDescription: lotInfo.productDescription,
          baseorCapital: lotInfo.baseorCapital,
          quantity: lotInfo.counterValue.toString(),
          isLotNumber: lotInfo?.isLotNumber,
          subInventoryCode: lotInfo?.subInventoryCode || '',
          partAddedFrom: lotInfo?.sourceLocation,
          sourceLocation: lotInfo?.sourceLocation,
          expirationDate: lotInfo.expirationDate,
          maxQuantity: lotInfo.maxPermissibleValue,
          iscatalog: true,
        });
      }
    });

    if (!lineItems.length) {
      setIsSaving(false);
      handleClose();
      return;
    }

    // The backend's ExpenseInventoryLineItems input only accepts a fixed set of fields;
    // UI-only fields (productDescription, baseorCapital, partAddedFrom, expirationDate,
    // maxQuantity, iscatalog) are stripped before sending. sfid/externalId are included, set only
    // on a prefilled inventory line, so the backend updates that line instead of inserting a new
    // one.
    const mutationLineItems = lineItems.map((item: any) => ({
      lotNumber: item.lotNumber,
      productId: item.productId,
      productNumber: item.productNumber,
      quantity: item.quantity,
      isLotNumber: item.isLotNumber,
      subInventoryCode: item.subInventoryCode,
      sourceLocation: item.sourceLocation,
      sfid: item.sfid,
      externalId: item.externalId,
    }));

    try {
      const result = await createUpdateExpenseLineItems({
        variables: {
          caseId: expenseInventoryDetail?.id,
          caseExternalId: expenseInventoryDetail?.caseExternalId,
          salesRepId: expenseInventoryDetail?.salesRepId,
          formType: 'New',
          lineItems: mutationLineItems,
        },
      });

      // The backend validates available quantity server-side and, when exceeded, rolls back
      // without inserting anything but still resolves (rather than throwing). Without this
      // check the modal would close silently as if the parts had been saved.
      if (result?.data?.createUpdateExpenseLineItems?.isMaxQuantityExceeded) {
        openSnackBar(
          NOTIFY_TYPE.ERROR,
          result.data.createUpdateExpenseLineItems.message ||
            'Error: The requested quantity exceeds what is available.'
        );
        return;
      }

      onPartsAdded?.(lineItems);
      handleClose();
    } catch (error) {
      if (isStaleRecordError(error)) {
        openSnackBar(NOTIFY_TYPE.ERROR, STALE_RECORD_MESSAGE);
      } else {
        openSnackBar(
          NOTIFY_TYPE.ERROR,
          (error as Error)?.message || 'Error: Expense Inventory parts could not be saved.'
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    setIsSaving,
    expenseInventoryDetail,
    inventoryPartsToAdd,
    catalogPartsToAdd,
    handleClose,
    openSnackBar,
    onPartsAdded,
    createUpdateExpenseLineItems,
  ]);

  return { onAddClick, loadingLineItems };
};

export default useSaveExpenseInventoryParts;
